import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Snackbar, Alert, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
  MenuItem, Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import config from '../../../config';

// HR Uniform Deposit — track refundable deposit + items issued to each
// employee and mark "Returned" with date when they hand it back. Pending
// deposits stay on the books as a company liability.
export default function UniformDeposit() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const companyId = company?.id;
  const today = new Date().toISOString().slice(0, 10);

  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empFilter, setEmpFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');  // Active | Returned | All

  const [issueDialog, setIssueDialog] = useState(false);
  const [issueForm, setIssueForm] = useState({
    employeeId: '', issuedDate: today, depositAmount: 0, uniformItems: '', remarks: '',
  });

  const [returnDialog, setReturnDialog] = useState(false);
  const [returnTarget, setReturnTarget] = useState(null);
  const [returnForm, setReturnForm] = useState({ returnDate: today, remarks: '' });

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const loadEmployees = async () => {
    const res = await fetch(`${config.API_BASE_URL}/Hr/Employees/${companyId}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setEmployees(await res.json());
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (empFilter) qs.append('employeeId', empFilter);
      if (statusFilter === 'Active') qs.append('isReturned', 'false');
      else if (statusFilter === 'Returned') qs.append('isReturned', 'true');
      const res = await fetch(`${config.API_BASE_URL}/Hr/UniformDeposits/${companyId}?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setRows(await res.json());
    } catch (e) { toast('Failed to load', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (companyId) loadEmployees(); /* eslint-disable-next-line */ }, [companyId]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId, empFilter, statusFilter]);

  const totals = useMemo(() => ({
    count: rows.length,
    deposit: rows.reduce((a, r) => a + Number(r.depositAmount || 0), 0),
    active: rows.filter((r) => !r.isReturned).reduce((a, r) => a + Number(r.depositAmount || 0), 0),
    returned: rows.filter((r) => r.isReturned).reduce((a, r) => a + Number(r.depositAmount || 0), 0),
  }), [rows]);

  const saveIssue = async () => {
    if (!issueForm.employeeId) { toast('Pick employee', 'warning'); return; }
    const body = {
      Id: null, CompanyId: companyId,
      EmployeeId: Number(issueForm.employeeId),
      IssuedDate: issueForm.issuedDate,
      DepositAmount: Number(issueForm.depositAmount) || 0,
      UniformItems: issueForm.uniformItems || null,
      Remarks: issueForm.remarks || null,
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/UniformDeposit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) { toast('Save failed', 'error'); return; }
    toast('Uniform issued');
    setIssueDialog(false);
    load();
  };

  const openReturn = (r) => {
    setReturnTarget(r);
    setReturnForm({ returnDate: today, remarks: r.remarks || '' });
    setReturnDialog(true);
  };

  const saveReturn = async () => {
    if (!returnTarget) return;
    const body = {
      Id: returnTarget.id, CompanyId: companyId,
      ReturnDate: returnForm.returnDate,
      Remarks: returnForm.remarks || null,
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/UniformDeposit/Return`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) { toast('Return failed', 'error'); return; }
    toast('Uniform returned');
    setReturnDialog(false);
    load();
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2, justifyContent: 'space-between' }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: '#475569', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckroomIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>Uniform Deposit</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>HR · {totals.count} record(s) · ₹{totals.deposit.toLocaleString('en-IN')} total</Typography>
          </Box>
        </Stack>
        <Button variant='contained' startIcon={<AddIcon />}
          onClick={() => { setIssueForm({ employeeId: '', issuedDate: today, depositAmount: 0, uniformItems: '', remarks: '' }); setIssueDialog(true); }}
          sx={{ bgcolor: '#475569', '&:hover': { bgcolor: '#334155' } }}>
          Issue Uniform
        </Button>
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          ['Active (held)', totals.active, '#dc2626'],
          ['Returned', totals.returned, '#16a34a'],
          ['Total', totals.deposit, '#1A56DB'],
        ].map(([label, val, color]) => (
          <Grid item xs={12} sm={4} key={label}>
            <Card sx={{ borderLeft: `4px solid ${color}`, p: 1.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>{label}</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>₹{Number(val).toLocaleString('en-IN')}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField select size='small' label='Employee' value={empFilter} onChange={(e) => setEmpFilter(e.target.value)} sx={{ bgcolor: '#fff', minWidth: 220 }}>
          <MenuItem value=''>All</MenuItem>
          {employees.map((e) => <MenuItem key={e.id} value={e.id}>{e.employeeName} ({e.employeeCode})</MenuItem>)}
        </TextField>
        <TextField select size='small' label='Status' value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ bgcolor: '#fff', minWidth: 160 }}>
          <MenuItem value='Active'>Active (held)</MenuItem>
          <MenuItem value='Returned'>Returned</MenuItem>
          <MenuItem value='All'>All</MenuItem>
        </TextField>
      </Stack>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Issued</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>Deposit</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Items</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Return</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && <TableRow><TableCell colSpan={9} align='center' sx={{ py: 4 }}><Typography sx={{ color: '#94a3b8' }}>No deposits.</Typography></TableCell></TableRow>}
                  {rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{new Date(r.issuedDate).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{r.employeeName}<Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{r.employeeCode}</Typography></TableCell>
                      <TableCell>{r.departmentName || '—'}</TableCell>
                      <TableCell align='right' sx={{ fontWeight: 700 }}>₹{Number(r.depositAmount).toLocaleString('en-IN')}</TableCell>
                      <TableCell sx={{ color: '#475569' }}>{r.uniformItems || '—'}</TableCell>
                      <TableCell>{r.returnDate ? new Date(r.returnDate).toLocaleDateString('en-IN') : '—'}</TableCell>
                      <TableCell>
                        <Chip size='small' label={r.isReturned ? 'Returned' : 'Held'}
                          sx={{ bgcolor: r.isReturned ? '#dcfce7' : '#fee2e2', color: r.isReturned ? '#166534' : '#991b1b', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ color: '#475569' }}>{r.remarks || '—'}</TableCell>
                      <TableCell align='right'>
                        {!r.isReturned && (
                          <Tooltip title='Mark returned'>
                            <IconButton size='small' onClick={() => openReturn(r)} sx={{ color: '#16a34a' }}><CheckCircleIcon sx={{ fontSize: 18 }} /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={issueDialog} onClose={() => setIssueDialog(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Issue Uniform</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField select size='small' fullWidth label='Employee *' value={issueForm.employeeId} onChange={(e) => setIssueForm({ ...issueForm, employeeId: e.target.value })}>
                {employees.map((e) => <MenuItem key={e.id} value={e.id}>{e.employeeName} ({e.employeeCode})</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField type='date' size='small' fullWidth label='Issued Date *' InputLabelProps={{ shrink: true }} value={issueForm.issuedDate} onChange={(e) => setIssueForm({ ...issueForm, issuedDate: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField type='number' size='small' fullWidth label='Deposit Amount *' value={issueForm.depositAmount} onChange={(e) => setIssueForm({ ...issueForm, depositAmount: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField size='small' fullWidth multiline rows={2} label='Uniform Items' helperText='e.g. 2 shirts, 1 pant, 1 belt' value={issueForm.uniformItems} onChange={(e) => setIssueForm({ ...issueForm, uniformItems: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField size='small' fullWidth multiline rows={2} label='Remarks' value={issueForm.remarks} onChange={(e) => setIssueForm({ ...issueForm, remarks: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setIssueDialog(false)}>Cancel</Button>
          <Button variant='contained' onClick={saveIssue} sx={{ bgcolor: '#475569', '&:hover': { bgcolor: '#334155' } }}>Issue</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={returnDialog} onClose={() => setReturnDialog(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Return Uniform
          {returnTarget && <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>{returnTarget.employeeName} · ₹{Number(returnTarget.depositAmount).toLocaleString('en-IN')}</Typography>}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField type='date' size='small' fullWidth label='Return Date *' InputLabelProps={{ shrink: true }} value={returnForm.returnDate} onChange={(e) => setReturnForm({ ...returnForm, returnDate: e.target.value })} />
            <TextField size='small' fullWidth multiline rows={2} label='Remarks' value={returnForm.remarks} onChange={(e) => setReturnForm({ ...returnForm, remarks: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setReturnDialog(false)}>Cancel</Button>
          <Button variant='contained' onClick={saveReturn} sx={{ bgcolor: '#16a34a' }}>Mark Returned</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
